import { ErrorApi } from "@core/modeles/model.ts";
import {
  ProfilFormData,
  EditProfilFormData,
} from "@modules/setting/userManagement/profil.model.ts";
import { ProfileService } from "@modules/setting/userManagement/profil.service.ts";
import { UserPostDTO } from "@modules/setting/userManagement/user.model.ts";
import {
  UserPostWithUserId,
  UserService,
} from "@modules/setting/userManagement/user.service.ts";
import { createAsyncThunk } from "@reduxjs/toolkit";

const userService = new UserService();
const profileService = new ProfileService();

interface Filter {
  page: number;
  size: number;
  sortBy?: string;
  sortDir?: string;
  search?: string;
  profil?: string;
  access?: string;
  localisation?: string;
}

export const getAllUsers = createAsyncThunk(
  "user/lists",
  async (filter: Filter) => {
    const userLists = await userService.getUserWithPaginate(
      filter.page,
      filter.size,
      filter.sortBy,
      filter.sortDir,
      filter.search,
      filter.profil,
      filter.access,
      filter.localisation
    );
    return userLists;
  }
);

export const getUserById = createAsyncThunk(
  "user/details",
  async (id: string) => {
    const user = await userService.findUserById(id);
    return user;
  }
);

export const getUserInfo = createAsyncThunk(
  "user/infos",
  async (id: string) => {
    const user = await userService.findUserById(id);
    return user;
  }
);

export const saveUser = createAsyncThunk(
  "user/save",
  async ({
    filter,
    onSuccess,
    onError,
  }: {
    filter: UserPostDTO;
    onSuccess: () => void;
    onError: (error: ErrorApi) => void;
  }) => {
    try {
      const userSaved = await userService.saveUser(filter);
      if (onSuccess) onSuccess();
      return userSaved;
    } catch (error) {
      return onError(error as ErrorApi);
    }
  }
);

export const editUser = createAsyncThunk(
  "user/edit",
  async ({
    filter,
    onSuccess,
    onError,
  }: {
    filter: UserPostWithUserId & { id: string };
    onSuccess: () => void;
    onError: (error: ErrorApi) => void;
  }) => {
    try {
      const { id, ...userData } = filter;
      const userSaved = await userService.editUser(id, userData);
      if (onSuccess) onSuccess();
      return userSaved;
    } catch (error) {
      onError(error as ErrorApi);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "user/delete",
  async ({
    id,
    onSuccess,
    onError,
  }: {
    id: string;
    onSuccess?: () => void;
    onError?: () => void;
  }) => {
    try {
      const userDeleted = await userService.deleteUser(id);
      if (onSuccess) onSuccess();
      return userDeleted;
    } catch {
      if (onError) onError();
    }
  }
);

export const getProfileById = createAsyncThunk(
  "profile/details",
  async (id: string) => {
    const profileById = await profileService.getProfilById(id);
    return profileById;
  }
);

export const getProfileList = createAsyncThunk(
  "profil/list",
  async (filter: Filter) => {
    const profileList = await profileService.getAllProfils(filter);
    return profileList;
  }
);

export const getProfilTransfer = createAsyncThunk(
  "profil/transfer",
  async () => {
    const listProfilTransfer = await profileService.getProfilTransfer();
    return listProfilTransfer;
  }
);

// Liste allégée des profils, accessible aux agents non-admin (contrairement à
// getProfileList qui tape GET /profil, réservé aux administrateurs).
export const getProfilLookup = createAsyncThunk("profil/lookup", async () => {
  return await profileService.getProfilLookup();
});

export const saveProfil = createAsyncThunk(
  "profile/save",
  async ({
    filter,
    onSuccess,
    onError,
  }: {
    filter: ProfilFormData;
    onSuccess: () => void;
    onError: (error: ErrorApi) => void;
  }) => {
    try {
      const profileSave = await profileService.saveProfilFormData(filter);
      if (onSuccess) onSuccess();
      return profileSave;
    } catch (error) {
      onError(error as ErrorApi);
    }
  }
);

export const editProfile = createAsyncThunk(
  "profile/edit",
  async (
    {
      filter,
      onSuccess,
      onError,
    }: {
      filter: EditProfilFormData & { id: string };
      onSuccess: () => void;
      onError: (error: ErrorApi) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      const { id, ...rest } = filter;
      const userSaved = await profileService.editProfileFormData(id, rest);
      onSuccess?.();
      return userSaved;
    } catch (error) {
      const typedError = error as ErrorApi;
      onError?.(typedError);
      return rejectWithValue(typedError);
    }
  }
);

export const deleteProfile = createAsyncThunk(
  "profile/delete",
  async ({
    id,
    onSuccess,
    onError,
  }: {
    id: string;
    onSuccess?: () => void;
    onError?: (error: ErrorApi) => void;
  }) => {
    try {
      const profileDeleted = await profileService.deleteProfile(id);
      if (onSuccess) onSuccess();
      return profileDeleted;
    } catch (error) {
      if (onError) onError(error as ErrorApi);
    }
  }
);

export const getPermissions = createAsyncThunk("permission/list", async () => {
  const permissionList = await profileService.getPermissions();
  return permissionList;
});

export const getAccess = createAsyncThunk("access/list", async () => {
  const accessList = await profileService.getAccess();
  return accessList;
});

export const getUserConnectedDetail = createAsyncThunk(
  "user/connectedDetails",
  async () => {
    const user = await userService.getUserConnectedDetail();
    return user;
  }
);
